<template>
  <div class="profile">
    <Loading v-if="loading" />
    <div class="profile_header">
      <Title :title="$t('profile')" />
      <img
        class="img"
        src="@/assets/icons/Qr-code.svg"
        alt="Qr-code"
        @click="openQrCode"
      />
    </div>

    <div class="profile__container">
      <FormKit
        id="profile-form"
        v-model="formData"
        type="form"
        :actions="false"
        @submit="submitHandler"
      >
        <Input
          v-for="el in infoFromUser"
          :key="el.id"
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :validation="el.validation"
          :validation-messages="{
            required: $t('validation.required')
          }"
          :data-testid="el.dataTestId"
        />
        <Input
          v-for="el in filteredInfoRoleAndPin"
          :key="el.id"
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :disabled="disabledInput(el.name)"
          :validation="el.validation"
          :validation-messages="{
            required: $t('validation.required'),
            length: $t('validation.length')
          }"
          :data-testid="el.dataTestId"
        />
      </FormKit>
      <div class="profile__container-buttons">
        <Button variant="filled" :title="$t('save')" @click="submitForm" />
        <Button
          variant="notBordered"
          :title="$t('cleanFields')"
          @click="cleanForm"
        />
      </div>
      <ChangeLang />
      <ChangePassword />
    </div>
  </div>
  <OpenQRCode
    :show-qr-code="showQrCode"
    :pharmacy-entry-pin="pharmacyEntryPin"
    :user-box-pin="userBoxPin"
    @close-modal="showQrCode = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import ChangeLang from './ChangeLang/ChangeLang.vue';
import ChangePassword from './ChangePassword/ChangePassword.vue';
import OpenQRCode from '@/duplicatePages/OpenQRCode/OpenQRCode.vue';

import { infoFromUser } from '@/constants';
import { infoRoleAndPin } from './configs';

import { DataUpdateUserProfile } from '@/store/modules/account/types';

@Options({
  name: 'Profile',
  components: {
    Loading,
    ChangeLang,
    ChangePassword,
    OpenQRCode
  },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        role: '',
        pharmacistPin: '',
        pinCode: ''
      },
      loading: false,
      showQrCode: false,
      userBoxPin: '',
      pharmacyEntryPin: '',
      infoFromUser,
      infoRoleAndPin
    };
  },
  created() {
    this.show();
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    filteredInfoRoleAndPin() {
      if (this.user?.role.name === 'chain_admin') {
        return infoRoleAndPin.filter(
          (el) => el.name !== 'pharmacistPin' && el.name !== 'pinCode'
        );
      }
      return infoRoleAndPin;
    }
  },
  methods: {
    ...mapActions('account', ['getUserProfile', 'updateProfile']),
    ...mapActions('pharmacy', ['getPharmacy']),
    async show() {
      this.loading = true;
      await this.getUserProfile();
      if (this.user?.role.name !== 'chain_admin') {
        await this.getPharmacy(this.user.pharmacyId);
      }

      this.formData = {
        ...this.formData,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        role: this.getFriendlyNamesRoles(this.user.role.name),
        pharmacistPin: this.user.pharmacistPin,
        pinCode: this.pharmacy?.pinCode
      };

      this.loading = false;
    },
    openQrCode() {
      this.showQrCode = true;

      this.pharmacyEntryPin = this.pharmacy.pinCode;
      this.userBoxPin = this.user.pharmacistPin;
    },
    getFriendlyNamesRoles(roleName: string) {
      const friendlyNames: Record<string, unknown> = {
        main_user: this.$t('mainUser'),
        user: this.$t('user'),
        chain_admin: this.$t('chainAdmin'),
        janitor: this.$t('janitor')
      };

      return friendlyNames[roleName];
    },
    async submitHandler(data: DataUpdateUserProfile) {
      this.loading = true;

      delete data.role;
      delete data.pinCode;

      await this.updateProfile(data);
      await this.getUserProfile();
      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('profile-form');
    },
    cleanForm() {
      this.formData.firstName = '';
      this.formData.lastName = '';
      this.formData.pharmacistPin = '';
    },
    disabledInput(name: string) {
      if (name === 'role' || name === 'pinCode') {
        return true;
      } else {
        return false;
      }
    }
  }
})
export default class Profile extends Vue {}
</script>

<style lang="scss">
.profile {
  padding: 24px;
  .profile_header {
    display: flex;
    .img {
      height: 20px;
      margin-left: 24px;
      cursor: pointer;
    }
  }
  .profile__container {
    padding-top: 32px;
    .formkit-form {
      .input {
        width: 377px;
        margin-bottom: 32px;
      }
    }
    .profile__container-buttons {
      margin-top: 30px;
    }
  }
}
</style>
